
import React from "react";
import { i18nManager } from "../../../../i18n";
import { useSearchParams } from "react-router-dom";
import { Schemes, Subscriber, Utils } from "../../../../utils";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../shared";
import { BoolHelper, ButtonManageRef, Classname, FormControl, FormGroup, ObjectHelper, Scheme, TextAreaInput } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ConfigurationType, IServiceConfiguration, ServiceConfigurationAndMappingService, SanitizedResponse } from "../../../../services";
import { ConfigurationInputs, ConfigurationValue } from "./ConfigUtils";

function ServiceConfigurations() {
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.ploutous.configurations.service_configs;
    const serviceConfigurationAndMappingService = ServiceConfigurationAndMappingService.getInstance();
    const [activeServiceConfiguration, setActiveServiceConfiguration] = React.useState<IServiceConfiguration>();
    const [serviceConfigurations, setServiceConfigurations] = React.useState<ApiResponseData<IServiceConfiguration>>({});
    labels.dialogs.manage.form.find((f) => f.name === "type")!.options = i18nManager.Labels.dashboard.ploutous.configurations.type_options.slice(1);
    labels.dialogs.manage.form.find((f) => f.name === "status")!.options = i18nManager.Labels.dashboard.ploutous.configurations.status_options.slice(1);
    labels.sided_labels.dropdown_form!.find((f) => f.control.name === "type")!.control.options = i18nManager.Labels.dashboard.ploutous.configurations.type_options;
    labels.sided_labels.dropdown_form!.find((f) => f.control.name === "status")!.control.options = i18nManager.Labels.dashboard.ploutous.configurations.status_options;

    return SidedContainer({
        searchParams,
        setSearchParams,
        labels: labels.sided_labels,
        data: serviceConfigurations,
        onAction: manageServiceConfiguration,
        queryData: fetchServiceConfigurations,
        activeData: (activeServiceConfiguration ? {
            data: activeServiceConfiguration,
            element: (<React.Fragment>
                <div className="header">
                    <i className="fa fa-times" onClick={() => {
                        setActiveServiceConfiguration(undefined);
                        Utils.updateSearchParams("active_data_id", undefined, setSearchParams);
                    }} />
                    <span className="name">{activeServiceConfiguration?.key}</span>
                    <span className="id" style={{ fontWeight: "bold" }}>{activeServiceConfiguration?.external_id}</span>
                    <span className="id">{activeServiceConfiguration?.type}</span>
                    <span className="desc">{activeServiceConfiguration?.description}{!!activeServiceConfiguration?.admin_write_only && (<span style={{ color: "#FF7171" }}> {labels.only_admin_can_modify}</span>)}</span>
                </div>
                <div className="middle actions" style={{ marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>
                    <span style={{ marginTop: 5 }}><b>{i18nManager.Labels.dashboard.ploutous.configurations.type_options.find((t) => t.value === activeServiceConfiguration.type)?.label}</b></span>
                    <ConfigurationValue type={activeServiceConfiguration.type} value={activeServiceConfiguration.options} />
                </div>
                <div className="middle actions" style={{ marginBottom: 0 }}>
                    <span style={{ marginTop: 5 }} onClick={() => manageServiceConfiguration(activeServiceConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.created_by}:</b> {activeServiceConfiguration?.created_by?.first_name} {activeServiceConfiguration?.created_by?.last_name} ({activeServiceConfiguration?.created_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageServiceConfiguration(activeServiceConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.updated_by}:</b> {activeServiceConfiguration?.updated_by?.first_name} {activeServiceConfiguration?.updated_by?.last_name} ({activeServiceConfiguration?.updated_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageServiceConfiguration(activeServiceConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.created_at}:</b> {Utils.formatToFineDate5(activeServiceConfiguration?.created_at)}</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageServiceConfiguration(activeServiceConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.updated_at}:</b> {Utils.formatToFineDate5(activeServiceConfiguration?.updated_at)}</span>
                </div>
                <div className="actions" style={{ overflow: "auto" }}>
                    <span className="link" onClick={() => manageServiceConfiguration(activeServiceConfiguration)}>{i18nManager.Labels.dashboard.ploutous.edit}</span>
                    {activeServiceConfiguration?.status !== "ACTIVE" && (<span className="link" onClick={() => activateDeactivateServiceConfiguration(activeServiceConfiguration, true)}>{i18nManager.Labels.dashboard.ploutous.activate}</span>)}
                    {activeServiceConfiguration?.status === "ACTIVE" && (<span className="link danger" onClick={() => activateDeactivateServiceConfiguration(activeServiceConfiguration, false)}>{i18nManager.Labels.dashboard.ploutous.deactivate}</span>)}
                    {activeServiceConfiguration?.status !== "DELETED" && (<span className="link danger" onClick={() => deleteServiceConfiguration(activeServiceConfiguration)}>{i18nManager.Labels.dashboard.ploutous.delete}</span>)}
                </div>
            </React.Fragment>)
        } : undefined),
        dataTemplate: (serviceConfiguration: IServiceConfiguration) => {
            return (<div className={Classname.build("channel", (activeServiceConfiguration?.external_id === serviceConfiguration.external_id ? "active" : null))} onClick={() => {
                setActiveServiceConfiguration(serviceConfiguration);
                Utils.updateSearchParams("active_data_id", serviceConfiguration.external_id, setSearchParams);
            }}>
                <div className="id" style={serviceConfiguration.status === "DELETED" ? { textDecoration: "line-through" } : {}}>
                    <span className="name" style={{ marginRight: 5 }}>{serviceConfiguration.key}</span> <span className="type">
                        - {serviceConfiguration.type}
                        {!!serviceConfiguration.admin_write_only && (` - ${labels.admin_write_only}`)}
                    </span>
                </div>
                <span className="desc">{serviceConfiguration.description}</span>
                <div className="ctrls">
                    <i className="fa fa-edit link gray" style={{ marginRight: 10 }} onClick={() => manageServiceConfiguration(serviceConfiguration)} />
                    <i className={Classname.build("fa fa-trash link danger", (serviceConfiguration.status === "DELETED" ? "noseur-disabled" : null))} style={{ marginRight: 10 }} onClick={() => deleteServiceConfiguration(serviceConfiguration)} />
                </div>
            </div>);
        }
    });

    function fetchServiceConfigurations() {
        const id = searchParams.get("active_data_id");
        serviceConfigurationAndMappingService.queryServiceConfigurations(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<IServiceConfiguration>) => {
            setServiceConfigurations(apiResponse.data!);
            if (id) for (const item of apiResponse.data?.content!) {
                if (item.external_id === id) {
                    setActiveServiceConfiguration(item);
                    //manageServiceConfiguration(item);
                    break;
                }
            }
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageServiceConfiguration(_serviceConfiguration: IServiceConfiguration = {} as any) {
        let changeProps: (type: ConfigurationType, v?: any) => void;
        const serviceConfiguration: IServiceConfiguration = ObjectHelper.clone(_serviceConfiguration);
        const basics = labels.dialogs.manage.basic[serviceConfiguration ? "update" : "create"];
        const renderValueComponent = !BoolHelper.equalsAny(serviceConfiguration.type, [ConfigurationType.TEXT, ConfigurationType.NUMBER]);

        const dialog = ConfirmDialog({
            ...basics,
            closable: true,
            rightStyle: { flex: 1.5 },
            confirmLabel: basics.confirm_label,
            style: { minWidth: (renderValueComponent ? 800 : 400), maxWidth: "80%" },
            content: (<div className="add-new-role" ref={(_) => {
                changeProps?.(serviceConfiguration.type, serviceConfiguration.options);
            }}>
                {Elements.buildConfirmDialogForm(labels.dialogs.manage.form, (name: string, value: string) => {
                    dialogErrorRef.current!.innerText = " ";
                    (serviceConfiguration as any)[name] = value;
                    if (name === "type") {
                        changeProps?.(value as ConfigurationType, serviceConfiguration.options);
                    }
                }, serviceConfiguration, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            rightContent: (renderValueComponent ? (<div style={{ display: "flex", flexDirection: "column", height: "100%", borderLeft: "1px solid #F0F0F0", marginTop: 30, marginLeft: 5, paddingLeft: 25 }}>
                <ConfigurationInputs type={ConfigurationType.TEXT} value={serviceConfiguration.options} changeProps={(cb) => changeProps = cb} onNewValue={(v) => {
                    serviceConfiguration.options = v;
                }} excludes={[ConfigurationType.TEXT, ConfigurationType.NUMBER]}/>
            </div>) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                let errorMessage;
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(serviceConfiguration).length < 5) {
                    errorMessage = labels.dialogs.manage.fill_required_fields;
                }
                if (errorMessage) {
                    dialogErrorRef.current!.innerText = errorMessage;
                    return false;
                }
                button?.setLoadingState(true);
                const method = (!!serviceConfiguration.external_id ? serviceConfigurationAndMappingService.updateServiceConfiguration : serviceConfigurationAndMappingService.createServiceConfiguration).bind(serviceConfigurationAndMappingService);
                method(serviceConfiguration)
                    .then(({ }: SanitizedResponse<IServiceConfiguration>) => {
                        Siren.alert(basics.successful);
                        dialog.hide(fetchServiceConfigurations);
                    }).catch(({ errors }: SanitizedResponse<any>) => {
                        dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                    }).finally(() => {
                        button?.setLoadingState(false);
                    });
                return false;
            }
        });
        dialog.show();
    }

    function activateDeactivateServiceConfiguration(serviceConfiguration: IServiceConfiguration, activate: boolean) {
        const dialogLabels = labels.dialogs[activate ? "activate" : "deactivate"];
        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmScheme: (activate ? Schemes.RIVTN_QUIRINUS : Scheme.DANGER),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                serviceConfigurationAndMappingService.updateServiceConfiguration({ external_id: serviceConfiguration.external_id, status: (activate ? "ACTIVE" : "IN_ACTIVE") }).then(() => {
                    Siren.alert(dialogLabels.successful);
                    dialog.hide(() => fetchServiceConfigurations());
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

    function deleteServiceConfiguration(serviceConfiguration: IServiceConfiguration) {
        const dialog = ConfirmDialog({
            closable: true,
            confirmScheme: Scheme.DANGER,
            desc: labels.dialogs.delete.desc,
            title: labels.dialogs.delete.title,
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                serviceConfigurationAndMappingService.deleteServiceConfiguration(serviceConfiguration.external_id).then(() => {
                    Siren.alert(labels.dialogs.delete.successful);
                    dialog.hide(() => setActiveServiceConfiguration(undefined));
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default ServiceConfigurations;

